import * as React from 'react'
import { closeDiscountModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect, useSelector } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { DisplayableError, ItemBlockDiscountType, LineItemDiscountType } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import SummaryContainer from '../Summary/SummaryContainer'
import SummaryItem from '../Summary/SummaryItem'
import NumberFormatter from '../../utilities/NumberFormatter'
import PercentInput from '../Form/PercentInput'
import { DEFAULT_CURRENCY } from '../../Constants'
import PowerSelect from '../Form/PowerSelect'
import LedgerItemHelper from '../../helpers/LedgerItemHelper'
import NumberInput from '../BlockEditor/RenderBlocks/Inputs/NumberInput'
import MoneyInput from '../Form/MoneyInput'

interface IStateToProps {
  currency: string
  total: number
  discountType?: LineItemDiscountType | ItemBlockDiscountType
  discount: number
  onSubmit?: (discountType: LineItemDiscountType | ItemBlockDiscountType, discount: number) => void
}

interface IDispatchToProps {
  close: typeof closeDiscountModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  discountType: LineItemDiscountType | ItemBlockDiscountType
  discount: number
  errors: DisplayableError[]
}

const DiscountModal = (props: IProps) => {
  const { discountType: propsDiscountType, discount: propsDiscount, currency, total } = props
  const { t } = useTranslation()
  const currentUser = useSelector((state: AppState) => state.authentication.currentUser)
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    discountType: propsDiscountType || LineItemDiscountType.PERCENTAGE,
    discount: propsDiscount || 0,
    errors: [],
  })
  const { workspace: { setting } } = currentUser
  const { didInitialLoad, discountType, discount, errors } = state

  let discountTotal = 0

  if (discountType === LineItemDiscountType.PERCENTAGE) {
    discountTotal = (total * discount) || 0
  } else {
    discountTotal = discount || 0
  }

  const newTotal = total - discountTotal

  React.useEffect(() => {
    setTimeout(() => {
      setState({ ...state, didInitialLoad: true })
    }, 500)
  }, [])

  const onCloseClick = () => {
    props.close()
  }

  const onDiscountTypeChange = (option: any) => {
    setState({
      ...state,
      discountType: option.value as LineItemDiscountType,
      discount: 0,
    })
  }

  const onDiscountPercentageChange = (discount) => {
    setState({ ...state, discount: discount })
  }

  const onDiscountFixedChange = (amount) => [
    setState({ ...state, discount: amount })
  ]

  const onFormSubmit = async (e) => {
    e.preventDefault()

    if (props.onSubmit) props.onSubmit(discountType, discount || 0)

    props.close()
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }


  const discountTypeOptions = LedgerItemHelper.getDiscountTypeOptions();
  const selectedDiscountTypeOption = discountTypeOptions.find(option => option.value === discountType);

  return (
    <ModalWindow>
      <ModalHeader
        title={t('DiscountModal::Discount')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('DiscountModal::Discount type')} <span>*</span></label>
                  <PowerSelect
                    options={discountTypeOptions}
                    value={selectedDiscountTypeOption}
                    onChange={onDiscountTypeChange}
                  />
                </div>
              </div>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('DiscountModal::Discount')} <span>*</span></label>
                  {discountType === LineItemDiscountType.PERCENTAGE && <PercentInput
                    defaultValue={discount}
                    placeholder={t('DiscountModal::Discount')}
                    onChange={onDiscountPercentageChange}
                    style={{ textAlign: 'left' }}
                  />}
                  {discountType === LineItemDiscountType.FIXED && <MoneyInput
                    name='rate'
                    currency={currency || DEFAULT_CURRENCY}
                    numberFormat={setting.number_format}
                    placeholderValue={0}
                    value={discount}
                    onBlur={onDiscountFixedChange}
                  />}
                </div>
              </div>
            </div>

            <div className='fields-section is-last'>
              <div className='fieldset'>
                <SummaryContainer columnCount={3}>
                  <SummaryItem
                    title={t('DiscountModal::Total')}
                    label={NumberFormatter.formatCurrency(currency || DEFAULT_CURRENCY, setting.number_format, total)}
                  />
                  <SummaryItem
                    title={t('DiscountModal::Discount')}
                    label={NumberFormatter.formatCurrency(currency || DEFAULT_CURRENCY, setting.number_format, discountTotal, { forcePrecision: 2 })}
                  />
                  <SummaryItem
                    title={t('DiscountModal::New total')}
                    label={NumberFormatter.formatCurrency(currency || DEFAULT_CURRENCY, setting.number_format, newTotal, { forcePrecision: 2 })}
                  />
                </SummaryContainer>
              </div>
            </div>

            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={t('DiscountModal::Save')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      discountModal: {
        currency,
        total,
        discountType,
        discount,
        onSubmit,
      }
    }
  } = state

  return {
    currency: currency,
    total: total,
    discountType: discountType,
    discount: discount,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeDiscountModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DiscountModal)